import { entitlementState, type EntitlementRow, type EntitlementState } from '@renalflow/core';
import { supabase } from '../auth/supabase';
import type { DbClient } from '../db/client';

const CACHE_KEY = 'entitlement_cache';

export interface Entitlement {
  state: EntitlementState;
  row: EntitlementRow | null;
  /** When the server last confirmed this. */
  checkedAt: string;
  source: 'server' | 'cache';
}

interface CachedEntitlement {
  row: EntitlementRow | null;
  checkedAt: string;
}

export function withTimeout<T>(promise: PromiseLike<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export class EntitlementUnavailableError extends Error {
  constructor() {
    super('Connect to the internet so RenalFlow can check your plan.');
  }
}

/**
 * The account's plan, from the server when reachable, otherwise from the copy cached on this
 * device (SPEC.md §6.2). The state is recomputed from the dates each time, so a cached yearly plan
 * still lapses on schedule while offline.
 */
export async function loadEntitlement(db: DbClient): Promise<Entitlement> {
  try {
    return await fetchEntitlement(db);
  } catch {
    const cached = await cachedEntitlement(db);
    if (!cached) throw new EntitlementUnavailableError();
    return cached;
  }
}

/** The plan as last confirmed on this device, or null if it has never been checked here. */
export async function cachedEntitlement(db: DbClient): Promise<Entitlement | null> {
  const raw = await db.getDeviceSetting(CACHE_KEY);
  if (!raw) return null;
  const cached = JSON.parse(raw) as CachedEntitlement;
  return { state: entitlementState(cached.row), row: cached.row, checkedAt: cached.checkedAt, source: 'cache' };
}

/** The plan from the server; updates the device cache. Throws when the server can't be reached. */
export async function fetchEntitlement(db: DbClient): Promise<Entitlement> {
  if (!navigator.onLine) throw new Error('offline');
  // While the network is down, supabase-js can stall requests behind its token refresh retries.
  // Never let that hold things up: give up after a few seconds.
  const { data, error } = await withTimeout(supabase.from('entitlements').select('plan, status, current_period_end').maybeSingle(), 6000);
  if (error) throw error;
  const cached: CachedEntitlement = { row: (data as EntitlementRow | null) ?? null, checkedAt: new Date().toISOString() };
  await db.setDeviceSetting(CACHE_KEY, JSON.stringify(cached));
  return { state: entitlementState(cached.row), row: cached.row, checkedAt: cached.checkedAt, source: 'server' };
}
